import { useTranslation } from 'react-i18next';
import ActiveSessions from './ActiveSessions';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { useForm } from '@mantine/form';
import CatalogEditCreateHeader from '../../components/CatalogEditCreateHeader/CatalogEditCreateHeader';
import CatalogEditCreateFooter from '../../components/CatalogEditCreateFooter/CatalogEditCreateFooter';
import {
  emailFormValidator,
  requiredFieldFormValidator,
  validateLength
} from '../../utils/validators/formValidators';
import { showNotification } from '../../utils/Notification/Notification';
import CatalogContainer from '../../components/CatalogContainer/CatalogContainer';
import { useSelector } from 'react-redux';
import { Card, Fieldset, NumberInput, PasswordInput, TextInput, Title } from '@mantine/core';
import { Grid } from '@mantine/core';
import { useUpdateUserById } from '../../hooks/users/mutations';

const EditPage = () => {
  const { t } = useTranslation(['ProfilePage', 'FormValidator']);
  const {
    user: { email, name, phoneNumber, _id }
  } = useSelector((state) => state.user);
  const updateById = useUpdateUserById(_id);
  const form = useForm({
    initialValues: {
      name: name || '',
      email: email || '',
      phoneNumber: phoneNumber || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      pinCode: ''
    },
    validate: {
      name: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      email: (value) => {
        if (value === '') return null;
        const emailValidatorRes = emailFormValidator(value);
        if (emailValidatorRes) return emailValidatorRes;
      },
      currentPassword: (value, values) => {
        if (values.newPassword || values.confirmPassword) {
          const requiredValidatorRes = requiredFieldFormValidator(value);
          if (requiredValidatorRes) return requiredValidatorRes;
        }
      },
      newPassword: (value, values) => {
        if (values.currentPassword || values.confirmPassword) {
          const requiredValidatorRes = requiredFieldFormValidator(value);
          if (requiredValidatorRes) return requiredValidatorRes;
        }
      },
      confirmPassword: (value, values) => {
        if (values.currentPassword || values.newPassword) {
          const requiredValidatorRes = requiredFieldFormValidator(value);
          if (requiredValidatorRes) return requiredValidatorRes;
          if (value !== values.newPassword) {
            return t('FormValidator:passwordsDoNotMatch');
          }
        }
      },

      pinCode: (value) => {
        if (!value) return null;
        const lengthValidatorRes = validateLength(4, 6, value.toString().length);
        if (lengthValidatorRes) return lengthValidatorRes;
      }
    }
  });

  type FormValues = typeof form.values;

  const submit = async (values: FormValues) => {
    try {
      const submitValues = {
        ...values,
        email: values.email.trim().toLowerCase(),
        pinCode: values.pinCode?.toString() || undefined
      };
      await updateById.mutateAsync(submitValues);
      showNotification({ type: 'Default', title: t('Notifications:success') });
      window.location.reload();
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  if (updateById.isPending) {
    return <LoaderOverlay visible />;
  }

  return (
    <CatalogContainer>
      <form onSubmit={form.onSubmit(submit)}>
        <CatalogEditCreateHeader title={`${t('ProfilePage:title')}`} />

        <Fieldset>
          <Grid gutter="md">
            <Grid.Col span={{ xs: 12, md: 6 }}>
              <Card>
                <Card.Section inheritPadding withBorder py="md">
                  <Title order={4}> {t('ProfilePage:CRUD.userInfoTitle')}</Title>
                </Card.Section>
                <Card.Section inheritPadding py="md">
                  <Grid>
                    <Grid.Col span={12}>
                      <TextInput
                        disabled
                        label={t('FieldName:email')}
                        placeholder={t('FieldName:email')}
                        {...form.getInputProps('email')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        label={t('FieldName:name')}
                        placeholder={t('FieldName:name')}
                        {...form.getInputProps('name')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        label={t('FieldName:phone')}
                        placeholder={t('FieldName:phone')}
                        {...form.getInputProps('phoneNumber')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <NumberInput
                        label={t('FieldName:pinCode')}
                        placeholder={t('FieldName:pinCode')}
                        minLength={4}
                        maxLength={6}
                        hideControls
                        {...form.getInputProps('pinCode')}
                      />
                    </Grid.Col>
                  </Grid>
                </Card.Section>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ xs: 12, md: 6 }}>
              <Card>
                <Card.Section inheritPadding withBorder py="md">
                  <Title order={4}> {t('ProfilePage:CRUD.passwordInfoTitle')}</Title>
                </Card.Section>
                <Card.Section inheritPadding py="md">
                  <Grid>
                    <Grid.Col span={12}>
                      <PasswordInput
                        label={t('FieldName:currentPassword')}
                        placeholder={t('FieldName:currentPassword')}
                        {...form.getInputProps('currentPassword')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <PasswordInput
                        label={t('FieldName:newPassword')}
                        placeholder={t('FieldName:newPassword')}
                        {...form.getInputProps('newPassword')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <PasswordInput
                        label={t('FieldName:confirmPassword')}
                        placeholder={t('FieldName:confirmPassword')}
                        {...form.getInputProps('confirmPassword')}
                      />
                    </Grid.Col>
                  </Grid>
                </Card.Section>
              </Card>
            </Grid.Col>
          </Grid>
        </Fieldset>
        <CatalogEditCreateFooter loading={updateById.isPending} />
      </form>
      <ActiveSessions />
    </CatalogContainer>
  );
};

export default EditPage;
